const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// ============================================================================
// AUTH HELPER
// ============================================================================
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

// ============================================================================
// DASHBOARD API - COMPLETE WITH ALL ENDPOINTS
// ============================================================================
export const dashboardAPI = {
  // ====== EXISTING CORE ENDPOINTS ======
  
  getDashboardData: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const response = await fetch(
      `${API_URL}/api/master_admin_dashbord/dashboard_data?${queryString}`,
      { headers: getAuthHeaders() }
    );
    if (!response.ok) throw new Error('Failed to fetch dashboard data');
    return response.json();
  },

  getAllTests: async (page = 1, limit = 10) => {
    const response = await fetch(
      `${API_URL}/api/master_admin_dashbord/all_test?page=${page}&limit=${limit}`,
      { headers: getAuthHeaders() }
    );
    if (!response.ok) throw new Error('Failed to fetch all tests');
    return response.json();
  },

  getRejectedTests: async (page = 1, limit = 10) => {
    const response = await fetch(
      `${API_URL}/api/master_admin_dashbord/rejected_test?page=${page}&limit=${limit}`,
      { headers: getAuthHeaders() }
    );
    if (!response.ok) throw new Error('Failed to fetch rejected tests');
    return response.json();
  },

  getHospitalData: async () => {
    const response = await fetch(
      `${API_URL}/api/master_admin_dashbord/hospital_data`,
      { headers: getAuthHeaders() }
    );
    if (!response.ok) throw new Error('Failed to fetch hospital data');
    return response.json();
  },

  getNursesData: async () => {
    const response = await fetch(
      `${API_URL}/api/master_admin_dashbord/nurse_detail_data`,
      { headers: getAuthHeaders() }
    );
    if (!response.ok) throw new Error('Failed to fetch nurses data');
    return response.json();
  },

  getReferredPatients: async (hospitalId, page = 1, limit = 10) => {
    const response = await fetch(
      `${API_URL}/api/master_admin_dashbord/patients_referred_to_hospital/${hospitalId}?page=${page}&limit=${limit}`,
      { headers: getAuthHeaders() }
    );
    if (!response.ok) throw new Error('Failed to fetch referred patients');
    return response.json();
  },

  // ====== ENHANCED FILTERING ENDPOINTS ======
  
  getDashboardDataFiltered: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const response = await fetch(
      `${API_URL}/api/master_admin_dashbord/dashboard_data_filtered?${queryString}`,
      { headers: getAuthHeaders() }
    );
    if (!response.ok) throw new Error('Failed to fetch filtered data');
    return response.json();
  },

  getFacilityComparison: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const response = await fetch(
      `${API_URL}/api/master_admin_dashbord/facility_comparison?${queryString}`,
      { headers: getAuthHeaders() }
    );
    if (!response.ok) throw new Error('Failed to fetch facility comparison');
    return response.json();
  },

  getMonthlyTrends: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const response = await fetch(
      `${API_URL}/api/master_admin_dashbord/monthly_trends?${queryString}`,
      { headers: getAuthHeaders() }
    );
    if (!response.ok) throw new Error('Failed to fetch monthly trends');
    return response.json();
  },

  getFacilityMonthlyStats: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const response = await fetch(
      `${API_URL}/api/master_admin_dashbord/facility_monthly_stats?${queryString}`,
      { headers: getAuthHeaders() }
    );
    if (!response.ok) throw new Error('Failed to fetch facility monthly stats');
    return response.json();
  },

  getAllFacilities: async () => {
    const response = await fetch(
      `${API_URL}/api/master_admin_dashbord/all_facilities`,
      { headers: getAuthHeaders() }
    );
    if (!response.ok) throw new Error('Failed to fetch all facilities');
    return response.json();
  },

  getRegionsList: async () => {
    const response = await fetch(
      `${API_URL}/api/master_admin_dashbord/regions_list`,
      { headers: getAuthHeaders() }
    );
    if (!response.ok) throw new Error('Failed to fetch regions list');
    return response.json();
  },

  getFacilityTypes: async () => {
    const response = await fetch(
      `${API_URL}/api/master_admin_dashbord/facility_types`,
      { headers: getAuthHeaders() }
    );
    if (!response.ok) throw new Error('Failed to fetch facility types');
    return response.json();
  },

  getDRClassificationReport: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const response = await fetch(
      `${API_URL}/api/master_admin_dashbord/dr_classification_report?${queryString}`,
      { headers: getAuthHeaders() }
    );
    if (!response.ok) throw new Error('Failed to fetch DR classification report');
    return response.json();
  },

  // ====== NEW ANALYTICS ENDPOINTS ======

  /**
   * Get trend analysis with growth rates
   * @param {Object} params - { region, facility_name, year }
   */
  getTrendAnalysis: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const response = await fetch(
      `${API_URL}/api/master_admin_dashbord/trend_analysis?${queryString}`,
      { headers: getAuthHeaders() }
    );
    if (!response.ok) throw new Error('Failed to fetch trend analysis');
    return response.json();
  },

  /**
   * Get facility performance scores
   * @param {Object} params - { region, year }
   */
  getFacilityPerformance: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const response = await fetch(
      `${API_URL}/api/master_admin_dashbord/facility_performance?${queryString}`,
      { headers: getAuthHeaders() }
    );
    if (!response.ok) throw new Error('Failed to fetch facility performance');
    return response.json();
  },

  /**
   * Get high-risk patient alerts
   * @param {Object} params - { region, facility_name }
   */
  getHighRiskAlerts: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const response = await fetch(
      `${API_URL}/api/master_admin_dashbord/high_risk_alerts?${queryString}`,
      { headers: getAuthHeaders() }
    );
    if (!response.ok) throw new Error('Failed to fetch high-risk alerts');
    return response.json();
  },

  /**
   * Get benchmark comparison for a facility
   * @param {string} facilityName - Required facility name
   * @param {Object} params - { year }
   */
  getBenchmarkComparison: async (facilityName, params = {}) => {
    const queryParams = { facility_name: facilityName, ...params };
    const queryString = new URLSearchParams(queryParams).toString();
    const response = await fetch(
      `${API_URL}/api/master_admin_dashbord/benchmark_comparison?${queryString}`,
      { headers: getAuthHeaders() }
    );
    if (!response.ok) throw new Error('Failed to fetch benchmark comparison');
    return response.json();
  },

  /**
   * Get dashboard notifications and alerts
   * @param {Object} params - { region, facility_name }
   */
  getDashboardNotifications: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const response = await fetch(
      `${API_URL}/api/master_admin_dashbord/dashboard_notifications?${queryString}`,
      { headers: getAuthHeaders() }
    );
    if (!response.ok) throw new Error('Failed to fetch notifications');
    return response.json();
  },

  /**
   * Get patient journey/history
   * @param {string} patientCode - Patient code
   */
  getPatientJourney: async (patientCode) => {
    const response = await fetch(
      `${API_URL}/api/master_admin_dashbord/patient_journey/${patientCode}`,
      { headers: getAuthHeaders() }
    );
    if (!response.ok) throw new Error('Failed to fetch patient journey');
    return response.json();
  },

  /**
   * Get screening gaps analysis
   * @param {Object} params - { region, facility_name }
   */
  getScreeningGaps: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const response = await fetch(
      `${API_URL}/api/master_admin_dashbord/screening_gaps?${queryString}`,
      { headers: getAuthHeaders() }
    );
    if (!response.ok) throw new Error('Failed to fetch screening gaps');
    return response.json();
  },

  /**
   * Get referral pathway analysis
   * @param {Object} params - { year }
   */
  getReferralPathwayAnalysis: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const response = await fetch(
      `${API_URL}/api/master_admin_dashbord/referral_pathway_analysis?${queryString}`,
      { headers: getAuthHeaders() }
    );
    if (!response.ok) throw new Error('Failed to fetch referral pathway analysis');
    return response.json();
  },

  /**
   * Get comprehensive dashboard data (all analytics in one call)
   * @param {Object} params - { region, facility_name, year }
   */
  getComprehensiveDashboard: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const response = await fetch(
      `${API_URL}/api/master_admin_dashbord/comprehensive_dashboard?${queryString}`,
      { headers: getAuthHeaders() }
    );
    if (!response.ok) throw new Error('Failed to fetch comprehensive dashboard');
    return response.json();
  },

  /**
   * Export filtered data to Excel
   * @param {Object} params - All filter parameters
   */
  exportFilteredData: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const response = await fetch(
      `${API_URL}/api/master_admin_dashbord/export_data?${queryString}`,
      { headers: getAuthHeaders() }
    );
    if (!response.ok) throw new Error('Failed to export data');
    return response;
  },

  /**
   * Get hospital referral statistics
   * @param {Object} params - { year }
   */
  getHospitalReferralStats: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const response = await fetch(
      `${API_URL}/api/master_admin_dashbord/hospital_referral_stats?${queryString}`,
      { headers: getAuthHeaders() }
    );
    if (!response.ok) throw new Error('Failed to fetch hospital stats');
    return response.json();
  },
};

// ============================================================================
// AUTH API
// ============================================================================
export const authAPI = {
  login: async (email, password) => {
    const response = await fetch(`${API_URL}/api/master_admin_dashbord/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email_id: email, password }),
    });
    return response.json();
  },

  getCurrentUser: () => {
    const token = localStorage.getItem('token');
    const userStr = localStorage.getItem('user');
    
    if (!token || !userStr) return null;
    
    try {
      return JSON.parse(userStr);
    } catch {
      return null;
    }
  },

  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  },
};

export default {
  dashboardAPI,
  authAPI,
};